import { resolveSignedPackRows, signedDocStatusOf } from '../../../utils/applicationSignedPdfPacks';

const SIGNED_DOC_BLOCKING = new Set(['PENDING_UPLOAD', 'SUBMITTED', 'REJECTED', 'RESUBMIT_REQUESTED']);

export function signedDocBlocksScreeningApproval(row) {
  const packs = resolveSignedPackRows(row);
  return packs.some((p) => SIGNED_DOC_BLOCKING.has(signedDocStatusOf(p.signed)));
}

/** @deprecated use signedDocBlocksScreeningApproval */
export function signedDocBlocksPhase1Approval(row) {
  return signedDocBlocksScreeningApproval(row);
}

export function signedDocStatusLabel(st, uploadSource) {
  const s = String(st || 'NOT_REQUESTED').toUpperCase();
  const source = String(uploadSource || '').toUpperCase();
  let submittedLabel = 'Awaiting review';
  if (source === 'COORDINATOR_VOLUNTARY') submittedLabel = 'Coordinator uploaded';
  else if (source === 'STAFF') submittedLabel = 'Staff uploaded';
  const map = {
    NOT_REQUESTED: 'Not requested',
    PENDING_UPLOAD: 'Awaiting upload',
    SUBMITTED: submittedLabel,
    VERIFIED: 'Verified',
    REJECTED: 'Rejected',
    RESUBMIT_REQUESTED: 'Resubmit requested',
  };
  return map[s] || s;
}

export function signedDocBadgeClass(st) {
  const s = String(st || 'NOT_REQUESTED').toUpperCase();
  if (s === 'VERIFIED') return 'bg-green-100 text-green-800 border-green-200';
  if (s === 'NOT_REQUESTED') return 'bg-gray-100 text-gray-600 border-gray-200';
  if (s === 'SUBMITTED') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (s === 'PENDING_UPLOAD' || s === 'RESUBMIT_REQUESTED') return 'bg-amber-100 text-amber-900 border-amber-200';
  if (s === 'REJECTED') return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
}

export function phaseBadgeClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED') return 'bg-green-100 text-green-800 border-green-200';
  if (s === 'REJECTED') return 'bg-red-100 text-red-800 border-red-200';
  if (s === 'HOLD') return 'bg-violet-100 text-violet-900 border-violet-200';
  return 'bg-amber-100 text-amber-900 border-amber-200';
}

export function screeningCardBorderClass(thisPhaseStatus) {
  const s = String(thisPhaseStatus || '').toUpperCase();
  if (s === 'HOLD') return 'border-violet-200 bg-violet-50/30';
  if (s === 'APPROVED') return 'border-green-200 bg-green-50/25';
  if (s === 'REJECTED') return 'border-red-200 bg-red-50/25';
  return 'border-gray-200 bg-white';
}

export function computeScreeningRowState(row, phase) {
  const p1 = String(row?.screeningPhase1?.status || 'PENDING').toUpperCase();
  const p2 = String(row?.screeningPhase2?.status || 'PENDING').toUpperCase();
  const thisPhaseStatus = phase === 1 ? p1 : p2;
  const phase1Ok = phase === 1 || p1 === 'APPROVED';
  const signedPackRows = resolveSignedPackRows(row);
  const approveBlockedBySignedDoc = (phase === 1 || phase === 2) && signedDocBlocksScreeningApproval(row);
  const canApprove = thisPhaseStatus === 'PENDING' && phase1Ok && !approveBlockedBySignedDoc;
  const canRejectOrHold = (thisPhaseStatus === 'PENDING' || thisPhaseStatus === 'HOLD') && phase1Ok;
  const canHold = thisPhaseStatus === 'PENDING' && phase1Ok;
  const canReleaseHold = thisPhaseStatus === 'HOLD' && phase1Ok;

  // Legacy single-pack fields (first / base) for older UI bits
  const primary = signedPackRows[0] || null;
  const signedDocSt = signedDocStatusOf(primary?.signed);
  const signedDocFileId = primary?.signed?.fileId || '';

  return {
    p1,
    p2,
    thisPhaseStatus,
    phase1Ok,
    signedPackRows,
    signedDocSt,
    signedDocFileId,
    canApprove,
    approveBlockedBySignedDoc,
    canRejectOrHold,
    canHold,
    canReleaseHold,
  };
}

export function getScreeningViewStorageKey(phase) {
  return `eventsScreeningPhase${phase}View`;
}

export function getInitialScreeningView(phase) {
  try {
    const stored = localStorage.getItem(getScreeningViewStorageKey(phase));
    return stored === 'card' || stored === 'list' ? stored : 'list';
  } catch {
    return 'list';
  }
}

export const LIST_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
export const CARD_EVENT_PAGE_SIZE_OPTIONS = [10, 20, 50];
export const EVENT_SUBMISSION_BATCH_OPTIONS = [25, 50, 100];

export function getScreeningPageLimitStorageKey(phase, kind) {
  return `eventsScreeningPhase${phase}${kind}PageLimit`;
}

export function getStoredScreeningPageLimit(phase, kind, defaultValue, allowed = []) {
  try {
    const raw = localStorage.getItem(getScreeningPageLimitStorageKey(phase, kind));
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return defaultValue;
    if (allowed.length && !allowed.includes(n)) return defaultValue;
    return n;
  } catch {
    return defaultValue;
  }
}

export function persistScreeningPageLimit(phase, kind, value) {
  try {
    localStorage.setItem(getScreeningPageLimitStorageKey(phase, kind), String(value));
  } catch {
    /* ignore */
  }
}

/** Group current page rows by event for card view. */
export function groupScreeningRowsByEvent(rows) {
  const map = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const eventId = String(row?.event?._id || row?.eventId || 'unknown');
    if (!map.has(eventId)) {
      map.set(eventId, {
        eventId,
        event: row?.event || {
          _id: eventId,
          name: row?.eventName || 'Unknown event',
          eventYear: row?.eventYear || '',
        },
        submissions: [],
      });
    }
    map.get(eventId).submissions.push(row);
  }
  return [...map.values()].sort((a, b) => {
    const nameA = `${a.event?.name || ''} ${a.event?.eventYear || ''}`.trim();
    const nameB = `${b.event?.name || ''} ${b.event?.eventYear || ''}`.trim();
    return nameA.localeCompare(nameB);
  });
}
